import { FC, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, useOutletContext, useSearchParams } from "react-router-dom"
import { AlertType } from "./types"

import { setCaseRAtype } from "@/UDTreat/udTreatSlice"

import { UButton, UText } from "../../../components"
import { Box, Grid } from "../../../components/mui.components"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { getBundleByCaseId } from "../../../core/app/slices/bundle/bundleThunkApi"
import { resetCasedata } from "../../../core/app/slices/case/caseSlice"
import {
  getCaseByPatient,
  getCaseFilesByCaseId,
  updateCaseStatus,
} from "../../../core/app/slices/case/caseThunkApi"
import { resetCase } from "../../../core/app/slices/clinical/clinicalSlice"
import { setRouteFrom } from "../../../core/app/slices/ConfigSlice"
import { getOrdersByCaseId } from "../../../core/app/slices/order/orderThunkApi"
import { addNewPatient, fetchPatientById } from "../../../core/app/slices/patients"
import { setData, setSelectedCaseIdInModal } from "../../../core/app/slices/patients/patientSlice"
import { resetPhotoLists } from "../../../core/app/slices/records/photograph/photographSlice"
import { resetXrays } from "../../../core/app/slices/records/xrays/xraysSlice"
import { RootState } from "../../../core/app/store"
import { ICase } from "../../../core/model/interface/ICase"
import { UI_PERMISSION } from "../../../core/utils/access/permissionConstants"
import { useMyNavigation } from "../../../hooks/useMyNavigation"
import usePermissions from "../../../hooks/userPermission"
import { CASE_STATUS } from "../config/status.config"
import { downloadZipFile } from "../PatientList/util/commonUtil"
import { navigateTo } from "../utils"
import { useTabRef } from "@/routes/TabProvider"
import { openCaseTab } from "@/modules/Patient/config/tab.config"
import { useTabRefresh } from "@/hooks/useTabRefresh"

import CaseActionPopup from "./components/CaseActionPopup"
import CaseOverview from "./components/CaseOverview"
import {
  resetPrescriptionSchema,
  resetSchema,
} from "@/core/app/slices/uassist/formSlice"
import { TabExceededPopup } from "../PatientList/components/UTable/TabExceededPopup"

const RenderByCaseStatus: FC = () => {
  const { hasAny } = usePermissions()
  const { t } = useTranslation()
  const { patientId:patientIdFromURL, caseId:caseIdFromURL, orgId } = useParams()
  const dispatch = useAppDispatch()
  const { caseList:caseListInit, caseListFetching } = useAppSelector(
    (state: RootState) => state.caseService,
  )
  const { patientData, isExistingPatientCheckModal } = useAppSelector(
    (state: RootState) => state.patientService,
  )
  const { user } = useAppSelector((state: RootState) => state.userService)
  const navigate = useMyNavigation()
  const tabRefs = useTabRef()
  const [searchParams] = useSearchParams()
  const [openTabExceededModal, setOpenTabExceededModal] = useState(false)

  /* ****
   * Get alert state passed from Parent(through Outlet)
   * If not needed in parent and is not passed, 
   * create and use local useState
   */
  const outletContextProps = useOutletContext<{
    isAlertOpen: AlertType
    setIsAlertOpen: (type: AlertType) => void
  }>()

  const localAlertState = useState<AlertType>("")

  const isAlertOpen = outletContextProps?.isAlertOpen || localAlertState[0]
  const setIsAlertOpen = outletContextProps?.setIsAlertOpen || localAlertState[1]
  // ****

  const patientId = useMemo(() => isExistingPatientCheckModal ? patientData?.id : patientIdFromURL, [patientIdFromURL, isExistingPatientCheckModal, patientData])

  const [caseIdInFocus, setCaseIdInFocus] = useState<string | null>("")
  const [assignedTo, setAssignedTo] = useState<number | null>(null)
  const [phaseId, setPhaseId] = useState<number | null>(null)

  const { selectedCaseIdInModal } = useAppSelector((state: RootState) => state.patientService)

  useEffect(() => {
    dispatch(resetCasedata())
    dispatch(setRouteFrom(""))
  }, [dispatch])

  const caseList = useMemo(()=>{

    if (!caseIdInFocus || !caseListInit.find((caseObj)=>String(caseObj.id)==caseIdInFocus)) return caseListInit

    const moveFirst = (arr, _caseId) => {
      const element = arr.find((caseObj)=>String(caseObj.id)==_caseId);
      const remaining = arr.filter((caseObj)=>String(caseObj.id)!==_caseId);
      return [element, ...remaining];
    };
    return moveFirst(caseListInit, caseIdInFocus)
  },[caseListInit, caseIdInFocus])

  useEffect(() => {
    if (!!caseIdInFocus && caseList.length > 0) {
      const caseObj = caseList.find(caseObj=>caseObj.id==caseIdInFocus)
      if(!caseObj){
        // the caseIdInFocus from the URL is not present in our caseList. 
        // redirect to url with no caseIdInFocus. renders in default sort order
        !isExistingPatientCheckModal && navigate(`/overview/patient/${patientId}?${searchParams}`, {replace: true})
        return
      }
      if(!caseObj.assigned_to){
        setIsAlertOpen("OPEN_ASSIGN_MODEL")
      }
    }
  }, [caseList, caseIdInFocus])

  useEffect(() => {

    // Determine the caseIdInFocus, based on the : caseIdFromURL, selectedCaseIdInModal. 
    let newCaseId = null

    if (isExistingPatientCheckModal) {
      /*** In Existing Patient Check Modal **/
      if (selectedCaseIdInModal && caseListInit.find((caseObj) => String(caseObj.id) === selectedCaseIdInModal)) { 
        // if valid selectedCaseIdInModal is present, and it is present in the caseList, use it
        newCaseId = selectedCaseIdInModal   
      } else if (caseListInit[0]?.id) {
        // if the first case in the list is present, use it
        newCaseId = String(caseListInit[0]?.id)
      } else {
        // if nothing else, set null
        newCaseId = null
      }
    } else {
      /*** In Patient Overview **/
      if (caseIdFromURL) {
        // if caseIdFromURL is present, use it
        newCaseId = caseIdFromURL
      } else if (caseListInit[0]?.id) {
        // if the first case in the list is present, use it
        newCaseId = String(caseListInit[0]?.id)
      } else {
        // if nothing else, set null
        newCaseId = caseListInit[0]?.id ? String(caseListInit[0]?.id) : null
      }
    }

    newCaseId && newCaseId !== caseIdInFocus && setCaseIdInFocus(newCaseId)

  }, [caseListInit, caseIdFromURL, selectedCaseIdInModal, isExistingPatientCheckModal])

  useEffect(()=>{
    if(isExistingPatientCheckModal){       // In Existing Patient Check Modal, save the caseIdInFocus
      dispatch(setSelectedCaseIdInModal(caseIdInFocus))
    }
  },[caseIdInFocus, isExistingPatientCheckModal])

  // Automatically refresh case data when tabs are closed and user returns to this tab
  useTabRefresh(() => {
    if (patientId) {
      dispatch(getCaseByPatient({ patientId, isCaseArchived: true }))
    }
  })

  const checkforActiveCase = () => {
    const payload = {
      firstname: patientData.firstName,
      lastname: patientData.lastName,
      dob: patientData.dob,
      patient_id: patientData.id,
      assigned_to: caseList[0]?.assigned_to || null, // set assignee same as recent case's assignee, for new case
    }
    dispatch(addNewPatient(payload)).then((res) => {
      if (res.payload?.data?.status === "Success") {
        dispatch(setData({ newPatientAPI: "idle" }))
        dispatch(getCaseByPatient({ patientId: patientId, isCaseArchived: true }))
        dispatch(fetchPatientById({ patientId }))
      } else {
        setIsAlertOpen("addNewCase")
      }
    })
  }

  const executeAction = (action: string, caseObj: ICase) => {
    setCaseIdInFocus(caseObj.id.toString())
    setAssignedTo(caseObj.assigned_to)
    setPhaseId(caseObj.phase)
    dispatch(setRouteFrom("PATIENT_OVERVIEW_MODULE"))
    const udesign_json = JSON.parse(caseObj.udesign_json)
    const isretainerOnly = udesign_json?.UpperType === "R" && udesign_json?.LowerType === "R"
    const pId = patientId || patientData.id

    // Helper function to open case tab with common parameters
    const openTab = (route: string) => {
      openCaseTab({
        route,
        tabRefs,
        setOpenTabExceededModal,
        orgId,
        isAllowToViewAllOrg: user?.isAllowToViewAllOrg,
      })
    }

    // Helper function to reset form data
    const resetData = () => {
      dispatch(resetXrays())
      dispatch(resetPhotoLists())
      dispatch(resetCase())
      dispatch(resetPrescriptionSchema())
      dispatch(resetSchema())
    }

    // Helper function to reset form data with RA type
    const resetFormData = () => {
      dispatch(setCaseRAtype(""))
      resetData()
    }

    // Helper function to download case files
    const downloadCaseFiles = (patientId: number, caseId: number) => {
      dispatch(
        getCaseFilesByCaseId({ patientId: patientId, caseId: caseId }),
      ).then((res) => {
        downloadZipFile(res.payload.data, caseId)
      })
    }

    // Helper function to update case status and refresh data
    const updateStatus = (caseDisposition: string) => {
      dispatch(
        updateCaseStatus({
          patientId: pId.toString(),
          caseId: caseObj.id.toString(),
          payload: { case_disposition: caseDisposition },
        }),
      ).then((res) => {
        if (res?.payload?.response_code === 200) {
          dispatch(getCaseByPatient({ patientId: pId }))
          dispatch(getOrdersByCaseId(caseObj.id))
          dispatch(getBundleByCaseId(caseObj.id))
        }
      })
    }

    switch (action) {
      case "OPEN_RECORDS":
        openTab(`/patients/choosetype/patient/${pId}/case/${caseObj.id}?from=overview`)
        break

      case "OPEN_PRESCRIPTION_FORM":
        resetFormData()
        if(caseObj.isRefinementReady){
          openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview&doRefinement=true`)
        } else {
          openTab(`/records/patient/${pId}/case/${caseObj.id}/rxForm/prescription?from=overview`)
        }
        break

      case "OPEN_SUMMARY":
        if(caseObj.isRefinementReady){
          openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview&doRefinement=true`)
        } else {
          const summaryRoute = 'summary'
          openTab(`/records/patient/${pId}/case/${caseObj.id}/rxForm/${summaryRoute}?from=overview`)
        }
        break

      case "OPEN_RETAINER_FORM":
        navigateTo("RETAINER", navigate, pId, caseObj.id)
        break

      case "START_REFINEMENT":
        resetData()
        openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview&doRefinement=true`)
        break

      case "OPEN_ARCHIVE_PATIENT_MODEL":
      case "OPEN_CASE_ARCHIVE_MODEL":
      case "OPEN_CANCEL_MODEL":
      case "OPEN_CANCEL_UASSIST_MODEL":
      case "OPEN_CASE_UNARCHIVE_MODEL":
      case "UNLOCK_CASE":
      case "DEACTIVATE_CASE":
      case "CHANGE_WORKFLOW":
        setIsAlertOpen(action)
        break

      case "ADD_NEW_CASE_MODEL":
        dispatch(setRouteFrom(""))
        checkforActiveCase()
        break

      case "OPEN_TREAT_PAGE":
        openTab(`/clinical/patient/${pId}/case/${caseObj.id}/treat?from=overview`)
        break

      case "UPDATE_STATUS_DELIVERED_TO_IN_PROGRESS":
        updateStatus(CASE_STATUS.TREATMENT_IN_PROGRESS)
        break

      case "UPDATE_STATUS_IN_PROGRESS_TO_COMPLETE":
        updateStatus(CASE_STATUS.TREATMENT_COMPLETE)
        break

      case "DOWNLOAD_FILES":
        downloadCaseFiles(Number(pId), caseObj?.id)
        break

      default:
        console.log(action, "..action")
    }
  }

  return (
    <>
      {!!caseList.length &&
        caseList?.map((caseObj: ICase, index: number) => (
          <Grid item mb={5} key={index}>
            <CaseOverview
              index={index}
              caseObj={caseObj}
              setIsAlertOpen={setIsAlertOpen}
              executeAction={executeAction}
              patientId={patientId}
              caseIdInFocus={caseIdInFocus}
              setCaseIdInFocus={setCaseIdInFocus}
            />
          </Grid>
        ))}

      {!caseList.length && caseListFetching === "succeeded" && (
        <Grid item mb={5}>
          {!isExistingPatientCheckModal && (
            <Grid
              item
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"flex-end"}
              sx={{ mb: 3 }}
            >
              {hasAny(UI_PERMISSION.UI_TXPLANS_CREATE) && (
                <UButton
                  variant="text"
                  disabled={patientData?.status === "ARCHIVED"}
                  onClickHandler={() => {
                    checkforActiveCase()
                  }}
                  sxProp={{
                    height: 30,
                    width: 113,
                    padding: "5px",
                    fontSize: "13px",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  btnText={t("overview.buttons.addNewCaseBtn")}
                />
              )}
              {hasAny(UI_PERMISSION.UI_TXPLANS_CLINICALVIEW) && (
                <UButton
                  variant="contained"
                  disabled={
                    patientData?.status === "ARCHIVED" || !caseList.length
                  }
                  sxProp={{
                    height: 30,
                    width: 95,
                    padding: "4px 10px",
                    fontSize: "13px",
                    ml: 2,
                  }}
                  btnText={t("overview.buttons.openCaseBtn")}
                  onClickHandler={() => {
                    console.log("NO Action Required for")
                  }}
                />
              )}
            </Grid>
          )}
          <Grid item>
            <Box
              sx={{
                padding: "24px 40px",
                minHeight: "220px",
                backgroundColor: "background.default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "16px",
                mb: 3,
              }}
            >
              {caseListFetching === "succeeded" && (
                <Grid
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <UText variant="body1" sxProp={{ color: "text.secondary" }}>
                    {"Please add a new case"}
                  </UText>
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
      <CaseActionPopup
        isAlertOpen={isAlertOpen}
        setIsAlertOpen={setIsAlertOpen}
        rowPatientId={patientId}
        caseId={caseIdInFocus}
        assignedTo={assignedTo}
        phaseId={phaseId}
        isReloadCallBack={() => {
          dispatch(getCaseByPatient({ patientId: patientId, isCaseArchived: true }))
        }}
      />
      <TabExceededPopup
        openTabExceededModal={openTabExceededModal}
        setOpenTabExceededModal={setOpenTabExceededModal}
      />
    </>
  )
}

export default RenderByCaseStatus
